import React from "react";
import ReactDOM from "react-dom";
import { defaultServiceContext } from "../../contexts";
import { DesignBranchingEventType, DesignMap } from "../../types";
import {
  addBranchingEvent,
  applyChangeMap
} from "../../utilities";
import Utility from "../../utilities/Utility";
import BranchMergeEventFactory from "../../utilities/versioning/BranchMergeEventFactory";
import { createDesignChangeMap } from "../../utilities/versioning";
import { getChangeMapsConflicts } from "../../utilities/versioning/conflicts/functions/get-change-maps-conflicts";
import LoadingSpinner from "../components/LoadingSpinner";
import ChangesContainer from "../components/versioning/change-log/ChangesContainer";
import { MergeConflictDialog } from "./MergeConflictDialog";
import { MergeDialogType } from "./merge-dialog-props";

export {
  MergeDialogType
};

/**
 * Make types available in JSDocs without importing them since this is not a TS file.
 *
 * @import { MergeDialogProps } from './merge-dialog-props';
 */

const {
  avsService,
  loadingService
} = defaultServiceContext;

/**
 * UI for merging application versions with options for resolving conflicts 
 *
 * @class MergeDialog
 * @extends {React.Component}
 */
export class MergeDialog extends React.Component {
  _branchMergeEventFactory = new BranchMergeEventFactory();

  /**
   * Dialog for used in reference check
   * @module MergeDialog
   * @param {Object} props React props
   */
  constructor(props) {
    super(props);

    this.state = {
      currChangeMap: {},
      prevChangeMap: {},
      changeMapsLoaded: false,
      hasPreviousChanges: false,
      hasCurrentChanges: false
    };
  }

  /**
   * Opens the merge dialog and displays it.
   *
   * @param {MergeDialogProps}
   */
  static async open({
    defaultCommitMessage = '',
    newVersionNumber = null,
    onComplete = () => { },
    sourceDesign,
    sourceOriginDesign,
    targetDesign,
    targetOriginDesign,
    type = MergeDialogType.save
  }) {
    const showApplication = sourceDesign.application !== targetDesign.application;
    const sourceLabel = (showApplication) ? (
      `${sourceDesign.application} (${sourceDesign.version})`
    ) : sourceDesign.version;
    const targetLabel = (showApplication) ? (
      `${targetDesign.application} (${targetDesign.version})`
    ) : targetDesign.version;

    // Create change maps.
    let sourceChangeMap = createDesignChangeMap(
      sourceDesign,
      sourceOriginDesign
    );
    const targetChangeMap = createDesignChangeMap(
      targetDesign,
      targetOriginDesign
    );
    const conflictSections = getChangeMapsConflicts({
      source: sourceChangeMap,
      sourceDesignMap: new DesignMap(sourceDesign),
      target: targetChangeMap,
      targetDesignMap: new DesignMap(targetDesign)
    });

    console.log({
      sourceChangeMap,
      targetChangeMap,
      conflictSections
    });

    if (conflictSections.length) {
      sourceChangeMap = await MergeConflictDialog.open({
        conflictSections,
        sourceChangeMap,
        sourceLabel,
        targetChangeMap,
        targetLabel
      });

      if (sourceChangeMap == null) {
        // User cancelled the merge.
        onComplete();
        return;
      }
    }

    const dialogOpenLatestVersion = targetDesign.version;
    const saveAsNewVersion = !!newVersionNumber;
    newVersionNumber ??= sourceDesign.repoVersion;
    const dialogId = 'merge-dialog';
    const commitMessageId = `${dialogId}-commit-message`;
    const dialogTitle = [
      (type === MergeDialogType.pull) ? 'Pulling' : 'Merging',
      sourceLabel,
      'into',
      targetLabel
    ].join(' ');
    const buttonsSelector = '#w2ui-popup .w2ui-popup-buttons';

    window.apm.popup(
      [
        `<div>`,
        `   <span style="padding-right: 1em">${dialogTitle}</span>`,
        `</div>`
      ].join(''),
      `<div id="${dialogId}"></div>`,
      [
        `<div class="commit-message commit_message_container" id="${commitMessageId}"></div>`,
        `<button class="w2ui-btn secondary cancel-button"> ${window.l.popup.cancel} </button>`,
        `<button class="w2ui-btn primary save-button" disabled> Save </button>`
      ].join(''),
      [
        `#${dialogId}`,
        () => {
          // Dialog elements.
          const cancelButton = document.querySelector(`${buttonsSelector} .cancel-button`);
          const commitMessageContainer = document.getElementById(commitMessageId);
          const dialogContainer = document.getElementById(dialogId);
          const saveButton = document.querySelector(`${buttonsSelector} .save-button`);

          // State variables.
          let commitMessage = defaultCommitMessage;
          let mergeEnabled = false;

          const refreshSaveButtonState = () => {
            const isStateValid = (
              mergeEnabled
              && commitMessage.length >= 10
            );

            saveButton.disabled = !isStateValid;
          };

          const closeDialog = () => {
            ReactDOM.unmountComponentAtNode(dialogContainer);
            ReactDOM.unmountComponentAtNode(commitMessageContainer);
            w2popup.close();
          };

          const mergeDialog = ReactDOM.render(
            <MergeDialog
              newVersionNumber={newVersionNumber}
              saveAsNewVersion={saveAsNewVersion}
              setMergeEnabled={(enabled) => {
                mergeEnabled = enabled;
                refreshSaveButtonState();
              }}
              sourceChangeMap={sourceChangeMap}
              sourceDesign={sourceDesign}
              sourceOriginDesign={sourceOriginDesign}
              targetChangeMap={targetChangeMap}
              targetDesign={targetDesign}
              type={type}
            />,
            dialogContainer
          );

          ReactDOM.render(
            <>
              <p><span>*</span>Commit message is required for saving (at least 10 characters long)</p>
              <textarea
                defaultValue={commitMessage}
                onChange={(e) => {
                  commitMessage = e.target.value;
                  refreshSaveButtonState();
                }}
                placeholder="Enter a message about your changes"
              />
            </>,
            commitMessageContainer
          );

          cancelButton.addEventListener('click', () => {
            closeDialog();
            onComplete();
          });

          saveButton.addEventListener('click', async () => {
            saveButton.disabled = true;
            closeDialog();

            // CORE-34511 - Cancel save if Merge Dialog is opened before a different version save to avoid overwrite
            const { branchId } = targetDesign;
            const latestVersion = (
              await avsService.getLatestVersion(branchId)
            )?.version;
            const isStale = latestVersion !== dialogOpenLatestVersion;

            if (isStale) {
              window.apm.notification.post("error", "Latest version changed since Save was initiated. Click Save to re-compare your changes to the current latest version.", false);
              onComplete();
              return;
            }

            await loadingService.useLoading(
              async () => {
                const mergedVersion = mergeDialog.getMergedVersion();

                await window.colm.saveWithCollaboration({
                  changeLogMap: mergeDialog.getCurrentVersionChangeLogMaps(),
                  commitMessage,
                  mergedVersion,
                  newVersionNumber,
                  saveAsNewVersion
                });
              },
              'Saving to Repository ...'
            );

            onComplete();
          });

          // Adjust tab pane height, just after "commit-message" container is rendered to get its height:
          // The logic is to substract from 100% popup height all of the others container's heights.
          // As a result, we get with the exact Tab Pane (or tab body) container height.
          // Same approach is applied for "branch_and_merge_container".
          const popupHeight = $("div.w2ui-popup").prop("offsetHeight");
          const popupTitleContainerHeight = $("div.w2ui-popup-title").prop("offsetHeight");
          const navTabsContainerHeight = $("#merge-dialog .nav-tabs").prop("offsetHeight");
          const popupButtonsContainerHeight = $("div.w2ui-popup-buttons").prop("offsetHeight");
          const tabPaneHeight = popupHeight - (
            popupTitleContainerHeight
            + navTabsContainerHeight
            + popupButtonsContainerHeight
          );
          $("#prevChangesTab").css("height", `${tabPaneHeight}px`);
          $("#currChangesTab").css("height", `${tabPaneHeight}px`);
        }
      ],
      700, // height
      null, // node
      null, // treenode
      false // showClose
    );
  }

  componentDidMount() {
    this.initChangeMaps();
  }

  componentWillReceiveProps(nextProps) {
    if (
      nextProps.sourceChangeMap !== this.props.sourceChangeMap
      || nextProps.targetChangeMap !== this.props.targetChangeMap
    ) {
      this.initChangeMaps();
    }
  }

  /** Initialize merge by getting version changeMaps and identifying conflicts. */
  initChangeMaps() {
    const {
      sourceChangeMap,
      targetChangeMap
    } = this.props;

    this.setState({ currChangeMap: sourceChangeMap });
    this.setState({ hasCurrentChanges: Object.keys(sourceChangeMap).length > 0 });
    this.setState({ prevChangeMap: targetChangeMap });
    this.setState({ hasPreviousChanges: Object.keys(targetChangeMap).length > 0 });

    this.setState({ changeMapsLoaded: true });
    // callback to SaveWidget: enables/disabled Merge Button in popup UI
    this.props.setMergeEnabled?.(true);
  }

  /**
   * Get changelog map of two symetrical objects: previousChangesMap and savedChangesMap
   *
   * @returns {object} changelogMap
   */
  getCurrentVersionChangeLogMaps() {
    return [this.state.currChangeMap, this.state.prevChangeMap];
  }

  /**
   * Called externally by SaveWidget
   *
   * @returns {object} Merged application
   */
  getMergedVersion() {
    const {
      sourceDesign,
      sourceOriginDesign,
      targetDesign,
      type
    } = this.props;
    if (targetDesign) {
      let newVersion = _.cloneDeep(targetDesign);
      // Source is local when saving, but on merge/pull your local design is the target.
      const localDesign = (type === MergeDialogType.save) ? (
        sourceDesign
      ) : targetDesign;
      // newVersion requires $loki data for localStorage syncronization
      newVersion = Utility.setLokiAndMetaOnDesignVersion({
        targetVersion: newVersion,
        sourceVersion: localDesign
      });
      newVersion.author = window.apm.getDesignerAuthor();
      const timestamp = window.apm.timestamp;
      const {
        _id,
        application,
        version
      } = sourceDesign;

      switch (type) {
        case MergeDialogType.merge: {
          const mergeEvent = this._branchMergeEventFactory.getEvent(
            DesignBranchingEventType.merge,
            {
              application,
              author: newVersion.author,
              branchId: _id,
              branchVersion: version,
              timestamp
            }
          );
          addBranchingEvent(newVersion, mergeEvent);
          break;
        }
        case MergeDialogType.pull:
          const mergeEvent = this._branchMergeEventFactory.getEvent(
            DesignBranchingEventType.pull,
            {
              author: newVersion.author,
              parentApplication: application,
              timestamp,
              version
            }
          );
          addBranchingEvent(newVersion, mergeEvent);
          break;
        case MergeDialogType.save:
          // Get the latest timestamp from an array of branching events.
          const getLastBranchingTimestamp = (branching) => {
            return (branching ?? []).reduce((lastTimestamp, event) => {
              return Math.max(lastTimestamp, event.timestamp);
            }, 0);
          }
          // Get the latest timestamp from a map of branching events.
          const getLastBranchingMapTimestamp = (branchingMap) => {
            return Object.values(branchingMap ?? {}).reduce((lastTimestamp, events) => {
              return Math.max(lastTimestamp, getLastBranchingTimestamp(events));
            }, 0);
          }
          if ('branching' in sourceDesign) {
            // Take whichever one is more up to date.
            const takeSource = (
              getLastBranchingTimestamp(sourceDesign.branching)
              > getLastBranchingTimestamp(newVersion.branching)
            );
            newVersion.branching = (takeSource) ? (
              _.cloneDeep(sourceDesign.branching)
            ) : newVersion.branching;
          }

          if ('branchingMap' in sourceDesign) {
            // Take whichever one is more up to date.
            const takeSource = (
              getLastBranchingMapTimestamp(sourceDesign.branchingMap)
              > getLastBranchingMapTimestamp(newVersion.branchingMap)
            );
            newVersion.branchingMap = (takeSource) ? (
              _.cloneDeep(sourceDesign.branchingMap)
            ) : newVersion.branchingMap;
          }

          if ('branchingMerging' in sourceDesign) {
            // Take whichever one is more up to date.
            const takeSource = (
              sourceDesign.branchingMerging.lastLocalChange
              > (newVersion.branchingMerging?.lastLocalChange ?? 0)
            );
            newVersion.branchingMerging = (takeSource) ? (
              _.cloneDeep(sourceDesign.branchingMerging)
            ) : newVersion.branchingMerging;
          }
          break;
      }

      return applyChangeMap({
        changeMap: this.state.currChangeMap,
        design: newVersion
      });
    }

    const newVersion = { ...sourceDesign };
    newVersion.version = (sourceOriginDesign ?? sourceDesign).version;
    newVersion.repoVersion = (sourceOriginDesign ?? sourceDesign).version;
    newVersion.author = window.apm.getDesignerAuthor();

    // TODO: See CORE-30634
    // do not merge latest intentHandling, last save always wins
    // TODO: should be using changeMaps. Update changeMaps to have this data

    return applyChangeMap({
      changeMap: this.state.prevChangeMap,
      design: newVersion
    });
  }

  /**
   * Provides a filtered application-object changeMap based on excludeFromDisplay property
   *
   * @param {*} changeMap
   * @returns
   * @memberof MergeDialog
   */
  getFilteredApplicationComponentsChangeMap(changeMap) {
    const newChangeMap = {};

    Object.keys(changeMap).forEach(key => {
      if (!changeMap[key].excludeFromDisplay) {
        newChangeMap[key] = changeMap[key];
      }
    });

    return newChangeMap;
  }

  getCurrentChangesHeader() {
    const title = "Your Changes";
    const version = (this.props.saveAsNewVersion) ? this.props.newVersionNumber : window.apm.serialized.repoVersion;
    const author = window.apm.getDesignerAuthor();
    return (
      <h3>
        <span className="labels">
          <i className={"fe icon-file-text"}></i>
          <span>{title}</span>
          <i className={"fe icon-hash"}></i>
          <span>{version}</span>
          <i className={"fe icon-user"}></i>
          <span>{author}</span>
        </span>
      </h3>
    );
  }

  getEmptyMessage() {
    return (
      <div className="panel">
        <h5 className="emptyMessage">No changes in this version</h5>
      </div>
    );
  }
  returnSortedChanges(current, key) {
    const changeMap = current === "current" ? this.state.currChangeMap[key] : this.state.prevChangeMap[key];

    switch (key) {
      case "modules":
      case "ccxml":
        return Object.entries(changeMap);
      case "application-modules":
      case "application-objects":
        return Object.entries(this.getFilteredApplicationComponentsChangeMap(changeMap)).sort((a, b) => a[1].node.name < b[1].node.name ? 1 : -1).sort((a, b) => a[1].node._class > b[1].node._class ? 1 : -1);
      case "application-variables":
        return Object.entries(changeMap).sort((a, b) => a[1].node.name > b[1].node.name ? 1 : -1);
      case "appProperties":
        return Object.entries(changeMap).sort((a, b) => a[1].object.config > b[1].object.config ? 1 : -1);
      case "compoundIntents":
        return Object.entries(changeMap).sort((a, b) => a[1].object.name < b[1].object.name ? 1 : -1);
      case "functions":
      case "folders":
      case "graphQLSchemas":
      case "csvData":
      case "unitTests":
      case "dialogs":
        return Object.entries(changeMap).sort((a, b) => a[1].object.name > b[1].object.name ? 1 : -1);
      case "application-intents":
        return Object.entries(changeMap).sort((a, b) => a[1].node.name < b[1].node.name ? 1 : -1);
      case "fillerPrompts":
        return Object.entries(changeMap).sort((a, b) => a[0] > b[0] ? 1 : -1);
      case "globalIntents":
        return Object.entries(changeMap).sort((a, b) => a[1].object.moduleProperties.name < b[1].object.moduleProperties.name ? 1 : -1);
      case "branchingMap":
      case "languages":
      case "globalErrorHandling":
      case "globalExceptionHandling":
      case "globalLoopHandling":
      case "intentHandling":
      case "settings":
      case "applicationVersion":
        return changeMap;
    }
  }
  returnContainers(current, key) {
    const getLabel = Utility.getChangeLogLabel(key);
    const sortedChanges = this.returnSortedChanges(current, key);
    const notMapped = ["globalErrorHandling", "globalExceptionHandling", "globalLoopHandling", "intentHandling", "branchingMap", "branching"];
    const simpleChanges = ["languages", "applicationVersion"]

    if (key === "settings") {
      const changeMap = current === "current" ? this.state.currChangeMap : this.state.prevChangeMap;
      const clonedSettings = _.cloneDeep(changeMap['settings']);

      if (clonedSettings["intentHandling"]) {
        if (clonedSettings["intentHandling"].changeset) {
          clonedSettings["intentHandling"].changeset.forEach(cs => {
            const iKey = cs.key[0];
            clonedSettings[iKey] = clonedSettings["intentHandling"][iKey];
          });

          delete clonedSettings["intentHandling"];
        }
      }

      if (clonedSettings["globalLoopHandling"]) {
        Object.keys(clonedSettings["globalLoopHandling"]).forEach(iKey => {
          clonedSettings[iKey] = clonedSettings["globalLoopHandling"][iKey];
        });

        delete clonedSettings["globalLoopHandling"];
      }

      return (
        <div className="panel" key={Utility.uuid4()}>
          <h4>Settings</h4>
          {Object.keys(clonedSettings).sort((a, b) => a > b ? 1 : -1).map((key) => {
            return (
              <ChangesContainer
                listType={key}
                label={getLabel}
                changeMap={clonedSettings[key]}
                serialized={this.props.sourceDesign}
                key={Utility.uuid4()}
              />
            );
          })}
        </div>
      );
    }
    if (!simpleChanges.includes(key) && notMapped.indexOf(key) === -1) {
      return (
        <div className="panel" key={Utility.uuid4()}>
          <h4>{getLabel}</h4>
          {sortedChanges.map(cm => {
            return (
              <ChangesContainer
                listType={key}
                changeMap={cm}
                serialized={this.props.sourceDesign}
                key={Utility.uuid4()}
              />
            );
          })}
        </div>
      );
    }
    if (!simpleChanges.includes(key) && notMapped.indexOf(key) !== -1) {
      return (
        <div className="panel" key={Utility.uuid4()}>
          <h4>{getLabel}</h4>
          {(() => {
            if (this.props.type && this.props.type === "merge" && key === "branchingMap" ||
              this.props.type && this.props.type === "pull" && key === "branching") {
              return;
            }
            return (
              <ChangesContainer
                listType={key}
                changeMap={sortedChanges}
                key={Utility.uuid4()}
              />
            );
          })()}
        </div>
      );
    }
    if (simpleChanges.includes(key)) {
      return (
        <div className="panel" key={Utility.uuid4()}>
          <h4>{getLabel}</h4>

          <ChangesContainer
            listType={key}
            changeMap={[key, sortedChanges]}
            key={Utility.uuid4()}
          />
        </div>
      );
    }
  }

  getChangeMapKeys(changeMaps) {
    let changeMapKeys = Object.keys(changeMaps);

    if (this.props.type && this.props.type === "merge") {
      changeMapKeys = changeMapKeys.filter(key => key !== "branchingMap")
    }

    return changeMapKeys;
  }

  /**
   * React render
   * @returns {XML}
   */
  render() {
    const hasPreviousChanges = Object.keys(this.state.prevChangeMap).length !== 0;
    const reviewMode = this.props.displayMode === 'review';
    const changeMapsLoaded = this.state.changeMapsLoaded;

    const hasPreviousChangeMaps = window.apm.changeLogMaps;
    const shouldDisplayPreviousChanges = hasPreviousChanges && !this.props.saveAsNewVersion && hasPreviousChangeMaps;
    const currChangeMapKeys = (changeMapsLoaded && this.state.hasCurrentChanges) ? this.getChangeMapKeys(this.state.currChangeMap) : null;
    const prevChangeMapKeys = (changeMapsLoaded && shouldDisplayPreviousChanges) ? Object.keys(this.state.prevChangeMap) : null;

    return (
      <div key={"merge-dialog-container"} className={"merge-dialog"}>

        {!changeMapsLoaded &&
          <div style={{ margin: '30% auto', height: '100%' }}>
            <LoadingSpinner message={"Comparing versions..."} />
          </div>
        }

        <ul className="nav nav-tabs" style={{ backgroundColor: 'var(--color-background-lighter)' }}>
          {!reviewMode && <li className="nav-button active">
            <a href="#currChangesTab" data-toggle="tab">Your changes</a>
          </li>}

          <li className={`nav-button ${reviewMode ? "active" : ""}`}>
            <a href="#prevChangesTab" data-toggle="tab">Latest changes</a>
          </li>

        </ul>
        <div className="tab-content clearfix">
          <div className={`tab-pane ${reviewMode ? "active" : ""}`} id="prevChangesTab">
            {changeMapsLoaded && (
              <div className="changeLogPanel">
                {shouldDisplayPreviousChanges
                  ? prevChangeMapKeys.map((key) => {
                    return this.returnContainers('previous', key)
                  })
                  : this.getEmptyMessage()}
              </div>
            )}
            {changeMapsLoaded && this.state.hasCurrentChanges && reviewMode &&
              <div className="changeLogPanel">
                {this.state.prevChangeMap['unitTests'] &&
                  <div className="panel">
                    <h4>Unit Tests</h4>
                    {Object.entries(this.state.prevChangeMap['unitTests']).map(ut => {
                      return (
                        <ChangesContainer
                          listType="unitTests"
                          changeMap={ut}
                          key={Utility.uuid4()}
                        />
                      );
                    })}
                  </div>
                }

                {this.state.prevChangeMap['dialogs'] &&
                  <div className="panel">
                    <h4>Sample Dialogs</h4>
                    {Object.entries(this.state.prevChangeMap['dialogs']).sort((a, b) => a[1].node.name < b[1].node.name ? 1 : -1).sort((a, b) => a[1].node._class > b[1].node._class ? 1 : -1).map(d => {
                      return (
                        <ChangesContainer
                          listType="dialogs"
                          changeMap={d}
                          key={Utility.uuid4()}
                        />
                      );
                    })}
                  </div>
                }
              </div>
            }

          </div>
          {(reviewMode) ? null : (
            <div className="tab-pane active" id="currChangesTab">
              {changeMapsLoaded && this.state.hasCurrentChanges &&
                <div className="changeLogPanel">

                  {currChangeMapKeys.map(key => {
                    return this.returnContainers("current", key);
                  })}

                </div>
              }
              {changeMapsLoaded && !this.state.hasCurrentChanges &&
                <div className="changeLogPanel">
                  {this.getEmptyMessage()}
                </div>
              }
            </div>
          )}
        </div>
      </div>
    );

  }
}
